import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CaretCircleDoubleDown,
  CaretCircleDown,
  CaretCircleRight,
  CaretCircleUp,
  CaretCircleDoubleUp,
  Virus,
  House,
  PawPrint,
  CurrencyDollar,
  ExclamationMark,
} from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import { useVisitAllocation } from '../hooks/useVisitAllocation';
import CustomerCard from '../components/CustomerCard';
import WorkerAllocation from '../components/WorkerAllocation';
import WelfareCheck from '../components/WelfareCheck';
import './ServiceDetails.css';

const getTodayDateString = () => new Date().toISOString().split('T')[0];

const getCurrentTimeString = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const severityConfig = {
  1: { label: 'Very Low', className: 'risk-very-low', icon: CaretCircleDoubleDown },
  2: { label: 'Low', className: 'risk-low', icon: CaretCircleDown },
  3: { label: 'Medium', className: 'risk-medium', icon: CaretCircleRight },
  4: { label: 'High', className: 'risk-high', icon: CaretCircleUp },
  5: { label: 'Critical', className: 'risk-critical', icon: CaretCircleDoubleUp },
};

const categoryConfig = {
  Infection: { icon: Virus },
  Home: { icon: House },
  Pet: { icon: PawPrint },
  Funding: { icon: CurrencyDollar },
  Other: { icon: ExclamationMark },
  General: { icon: ExclamationMark },
  Environment: { icon: House },
  Allergy: { icon: ExclamationMark },
};

const ServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeUserEmail, activeUserRoles, activeEmployeeId, loading: authLoading } = useAuth();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRiskIds, setExpandedRiskIds] = useState({});
  const [formData, setFormData] = useState({
    serviceCompleted: '',
    checkInDate: getTodayDateString(),
    checkInTime: getCurrentTimeString(),
    checkOutTime: getCurrentTimeString(),
    completedAllTasks: '',
    notes: '',
    welfareConcern: '',
    welfareCheck: null,
    attendanceOutcome: '',
    canceled: '',
  });
  const allocation = useVisitAllocation((visitId, assignment) => {
    if (String(visitId) !== String(id)) return;
    setService((current) => current ? {
      ...current,
      workerId: assignment.workerId || null,
      worker: assignment.worker || null,
    } : current);
  });
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [attachmentError, setAttachmentError] = useState('');
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [removingAttachmentId, setRemovingAttachmentId] = useState(null);
  const previewUrls = useRef(new Set());

  useEffect(() => () => {
    previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrls.current.clear();
  }, []);

  useEffect(() => {
    if (authLoading) return;

    let isMounted = true;
    const loadServiceData = async () => {
      try {
        setLoading(true);
        let res = await fetch(`/api/jobs/${encodeURIComponent(id)}?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
          headers: {
            'x-kcs-user-email': activeUserEmail || '',
            'x-kcs-user-roles': (activeUserRoles || []).join(','),
            'x-kcs-provider-employee-id': activeEmployeeId || '167',
          },
        });
        if (!res.ok) {
          res = await fetch(`/api/services/${encodeURIComponent(id)}?type=job&employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
            headers: {
              'x-kcs-user-email': activeUserEmail || '',
              'x-kcs-user-roles': (activeUserRoles || []).join(','),
              'x-kcs-provider-employee-id': activeEmployeeId || '167',
            },
          });
        }
        if (res.ok) {
          const data = await res.json();
          if (isMounted) {
            setService(data);
            const validation = data.validation;
            setFormData({
              serviceCompleted: validation?.serviceCompleted || '',
              checkInDate: validation?.checkInDate || getTodayDateString(),
              checkInTime: validation?.checkInTime?.slice(0, 5) || getCurrentTimeString(),
              checkOutTime: validation?.checkOutTime?.slice(0, 5) || getCurrentTimeString(),
              completedAllTasks: validation?.completedAllTasks || '',
              notes: validation?.notes || '',
              welfareConcern: validation?.welfareConcern || '',
              welfareCheck: validation?.welfareCheck || null,
              attendanceOutcome: validation?.attendanceOutcome || '',
              canceled: validation?.canceled || '',
            });
            const persistedAttachments = (validation?.attachments || []).map((attachment) => ({
              ...attachment,
              id: attachment.id,
              name: attachment.name,
              type: attachment.type,
              size: attachment.size,
              preview: null,
            }));
            setAttachments(persistedAttachments);
            persistedAttachments.filter((attachment) => String(attachment.type || '').startsWith('image/')).forEach(async (attachment) => {
              try {
                const previewResponse = await fetch(`/api/jobs/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachment.id)}?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
                  headers: {
                    'x-kcs-user-email': activeUserEmail || '',
                    'x-kcs-user-roles': (activeUserRoles || []).join(','),
                    'x-kcs-provider-employee-id': activeEmployeeId || '167',
                  },
                });
                if (!previewResponse.ok) return;
                const preview = URL.createObjectURL(await previewResponse.blob());
                if (!isMounted) {
                  URL.revokeObjectURL(preview);
                  return;
                }
                previewUrls.current.add(preview);
                setAttachments((current) => current.map((item) => item.id === attachment.id ? { ...item, preview } : item));
              } catch (error) {
                console.warn(`Unable to load preview for attachment ${attachment.id}.`);
              }
            });
            persistedAttachments.filter((attachment) => String(attachment.type || '').startsWith('image/')).forEach(async (attachment) => {
              try {
                const response = await fetch(`/api/jobs/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachment.id)}?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
                  headers: {
                    'x-kcs-user-email': activeUserEmail || '',
                    'x-kcs-user-roles': (activeUserRoles || []).join(','),
                    'x-kcs-provider-employee-id': activeEmployeeId || '167',
                  },
                });
                if (!response.ok) return;
                const preview = URL.createObjectURL(await response.blob());
                if (!isMounted) {
                  URL.revokeObjectURL(preview);
                  return;
                }
                previewUrls.current.add(preview);
                setAttachments((current) => current.map((item) => item.id === attachment.id ? { ...item, preview } : item));
              } catch (error) {
                console.warn(`Unable to load preview for attachment ${attachment.id}.`);
              }
            });
            setSaveError('');
            setSaveMessage('');
          }
        } else {
          throw new Error('Service not found');
        }
      } catch (err) {
        console.error('Error loading service details:', err);
        if (isMounted) {
          setService(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadServiceData();

    return () => { isMounted = false; };
  }, [id, authLoading, activeEmployeeId, activeUserEmail, activeUserRoles]);

  const handleAttachmentChange = async (event) => {
    if (removingAttachmentId) return;
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    event.target.value = '';
    setAttachmentError('');
    setUploadingAttachments(true);
    try {
      for (const file of files) {
        if (!file.size || file.size > 20 * 1024 * 1024) {
          setAttachmentError(`${file.name} exceeds the 20 MiB per-file limit.`);
          continue;
        }

        const form = new FormData();
        form.append('file', file);
        const response = await fetch(`/api/jobs/${encodeURIComponent(id)}/attachments?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
          method: 'POST',
          body: form,
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || `Unable to upload ${file.name}.`);

        const attachment = result.attachment;
        const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
        if (preview) previewUrls.current.add(preview);
        setAttachments((current) => [...current, { ...attachment, preview }]);
      }
    } catch (error) {
      setAttachmentError(error.message || 'Unable to upload attachment.');
    } finally {
      setUploadingAttachments(false);
    }
  };

  const removeAttachment = async (attachmentId) => {
    if (removingAttachmentId) return;
    const removed = attachments.find((attachment) => attachment.id === attachmentId);
    if (!removed) return;

    setAttachmentError('');
    setRemovingAttachmentId(attachmentId);
    try {
      const response = await fetch(`/api/jobs/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
        method: 'DELETE',
        headers: {
          'x-kcs-user-email': activeUserEmail || '',
          'x-kcs-user-roles': (activeUserRoles || []).join(','),
          'x-kcs-provider-employee-id': activeEmployeeId || '167',
        },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to remove this attachment.');
      if (removed.preview) {
        URL.revokeObjectURL(removed.preview);
        previewUrls.current.delete(removed.preview);
      }
      setAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
    } catch (error) {
      setAttachmentError(error.message || 'Unable to remove this attachment.');
    } finally {
      setRemovingAttachmentId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getRisksArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data.risks)) return data.risks;
    if (Array.isArray(data.relevantRisks)) return data.relevantRisks;
    if (data.customer && Array.isArray(data.customer.risks)) return data.customer.risks;
    if (data.customer && Array.isArray(data.customer.relevantRisks)) return data.customer.relevantRisks;
    return [];
  };

  const rawRisks = getRisksArray(service);
  const visibleRisks = rawRisks.filter((r) => {
    if (!r) return false;
    if (typeof r === 'string') return true;
    return r.category !== 'Funding' && r.category !== 'Funder';
  });

  const toggleRiskExpand = (riskId) => {
    setExpandedRiskIds(prev => ({
      ...prev,
      [riskId]: !prev[riskId],
    }));
  };

  const validateDateTime = (dateValue, timeValue, fieldName) => {
    if (!dateValue || !timeValue) {
      alert(`Please provide a valid ${fieldName.toLowerCase()} date and time.`);
      return null;
    }

    const dateTimeString = `${dateValue}T${timeValue}`;
    const parsedValue = new Date(dateTimeString);

    if (Number.isNaN(parsedValue.getTime())) {
      alert(`Please enter a valid ${fieldName.toLowerCase()} date and time.`);
      return null;
    }

    return parsedValue.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (uploadingAttachments || removingAttachmentId) return;
    if (!formData.serviceCompleted) {
      alert('Please indicate if service was completed');
      return;
    }

    const isCompleted = formData.serviceCompleted === 'yes';
    const isAttended = isCompleted || ['AttendedPresent', 'AttendedNotHome'].includes(formData.attendanceOutcome);

    if (isCompleted) {
      const checkInTimestamp = validateDateTime(formData.checkInDate, formData.checkInTime, 'Check-in');
      const checkOutTimestamp = validateDateTime(formData.checkInDate, formData.checkOutTime, 'Check-out');

      if (!checkInTimestamp || !checkOutTimestamp) {
        return;
      }

      const checkInDateTime = new Date(checkInTimestamp);
      const checkOutDateTime = new Date(checkOutTimestamp);

      if (checkOutDateTime <= checkInDateTime) {
        alert('Check-out time must be later than check-in time.');
        return;
      }

      if (!formData.completedAllTasks || !formData.notes.trim() || !formData.welfareConcern) {
        alert('Please complete all required fields');
        return;
      }
    } else if (!formData.attendanceOutcome || !formData.notes.trim()) {
      alert('Please choose an attendance outcome and enter notes.');
      return;
    } else if (isAttended && !formData.welfareConcern) {
      alert('Please indicate whether there is a welfare concern.');
      return;
    } else if (isAttended && formData.welfareConcern === 'yes' && !['yes', 'no'].includes(formData.welfareCheck?.immediateConcerns)) {
      alert('Please answer the Immediate concerns question.');
      return;
    }

    const payload = {
      serviceCompleted: isCompleted ? 'yes' : 'no',
      attendanceOutcome: formData.attendanceOutcome,
      canceled: formData.canceled,
      checkInDate: formData.checkInDate,
      checkInTime: isCompleted ? formData.checkInTime : null,
      checkOutTime: isCompleted ? formData.checkOutTime : null,
      completedAllTasks: formData.completedAllTasks,
      notes: formData.notes,
      welfareConcern: isAttended ? formData.welfareConcern : 'no',
      welfareCheck: isAttended ? formData.welfareCheck : null,
      attachmentIds: attachments.map((attachment) => attachment.id),
    };

    setSaving(true);
    setSaveError('');
    setSaveMessage('');
    let returnAfterSave = false;
    try {
      const response = await fetch(`/api/jobs/${encodeURIComponent(id)}/validation?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to save this visit response.');
      setService((current) => current ? { ...current, validation: result.validation } : current);
      const integrations = result.validation?.welfareCheckIntegrations || {};
      const warnings = [];
      if (isAttended && integrations.alayaCareProgressNote?.status === 'failed') {
        warnings.push('The visit was saved, but the AlayaCare progress note could not be created.');
      }
      if (isAttended && formData.welfareConcern === 'yes' && integrations.powerAutomate?.status === 'not_configured') {
        warnings.push('Power Automate escalation is not configured.');
      } else if (isAttended && integrations.powerAutomate?.status === 'failed') {
        warnings.push('Power Automate escalation failed.');
      }
      setSaveMessage(integrations.alayaCareProgressNote?.status === 'sent'
        ? 'Visit response saved. AlayaCare progress note created.'
        : 'Visit response saved.');
      if (warnings.length) setSaveError(warnings.join(' '));
      returnAfterSave = warnings.length === 0;
    } catch (error) {
      setSaveError(error.message || 'Unable to save this visit response.');
    } finally {
      setSaving(false);
    }
    if (returnAfterSave) {
      const historyIndex = window.history.state?.idx;
      if (Number.isInteger(historyIndex) && historyIndex > 0) navigate(-1);
      else navigate('/provider/dashboard', { replace: true });
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!service) {
    return <div className="error">Service not found</div>;
  }

  return (
    <div className="service-details">
      <div className="page-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
        <h1>Edit Job</h1>
      </div>

      <div className="details-grid">
        <CustomerCard customer={service.customer} />

        <div className="info-card risks-card">
          <h2 className="card-title">Risks</h2>
          {visibleRisks.length === 0 ? (
            <p className="no-risks">No active risks</p>
          ) : (
            <ul className="risk-list">
              {visibleRisks.map((risk, index) => {
                const riskId = typeof risk === 'object' && risk.id ? risk.id : `risk-${index}`;
                const category = typeof risk === 'object' && risk.category ? risk.category : 'General';
                const severityVal = typeof risk === 'object' && typeof risk.severity === 'number' ? risk.severity : 3;
                const severity = severityConfig[severityVal] || severityConfig[3];
                const SeverityIcon = severity.icon;
                const CategoryIcon = categoryConfig[category]?.icon || ExclamationMark;
                const expanded = !!expandedRiskIds[riskId];
                const riskText = typeof risk === 'string' ? risk : (risk.risk || risk.description || risk.name || 'Risk noted');

                return (
                  <li key={riskId} className="risk-item-row">
                    <div className="risk-main-content">
                      <div className="risk-header-row">
                        <div className="risk-tag-group">
                          <span className="risk-category-pill">
                            <CategoryIcon size={16} weight="fill" />
                            {category}
                          </span>
                          <span className={`severity-pill ${severity.className}`}>
                            <SeverityIcon size={18} weight="fill" />
                            {severity.label}
                          </span>
                        </div>
                      </div>
                      <p className={`risk-description ${expanded ? 'expanded' : 'collapsed'}`}>
                        {riskText}
                      </p>
                    </div>
                    {(riskText || '').length > 140 && (
                      <button
                        type="button"
                        className={`risk-toggle ${expanded ? 'expanded' : ''}`}
                        onClick={() => toggleRiskExpand(riskId)}
                        aria-label={expanded ? 'Collapse risk' : 'Expand risk'}
                      >
                        <span>›</span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="info-card">
          <h2 className="card-title">Service Details</h2>
          <div className="info-row">
            <span className="label">Type:</span>
            <span className="value">{service.serviceType}</span>
          </div>
          <div className="info-row">
            <span className="label">Duration:</span>
            <span className="value">{service.duration || 'Not available'}</span>
          </div>
          <div className="info-row info-row-editable">
            <span className="label">Worker:</span>
            <WorkerAllocation
              item={{ ...service, customer: service.customer?.displayName || service.customer || 'Customer' }}
              {...allocation}
              saving={allocation.savingAssignments[service.id]}
              error={allocation.assignmentErrors[service.id]}
            />
            {allocation.workersError && <p role="alert">{allocation.workersError}</p>}
          </div>
          <div className="info-row">
            <span className="label">Instructions:</span>
            <span className="value">{service.instructions || 'No specific instructions provided.'}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="validation-form">
        <h2 className="form-title">Job Notes</h2>

        <div className="form-group">
          <label className="form-label required">Did you complete the service?</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="serviceCompleted"
                value="yes"
                checked={formData.serviceCompleted === 'yes'}
                onChange={handleInputChange}
              />
              Yes, completed
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="serviceCompleted"
                value="no"
                checked={formData.serviceCompleted === 'no'}
                onChange={handleInputChange}
              />
              No, unable to complete
            </label>
          </div>
        </div>

        {formData.serviceCompleted === 'yes' && (
          <>
            <div className="timestamp-row">
              <div className="timestamp-field">
                <label className="form-label">Check-in date</label>
                <input
                  type="date"
                  value={formData.checkInDate}
                  onChange={handleInputChange}
                  className="timestamp-input"
                  name="checkInDate"
                />
              </div>

              <div className="timestamp-field">
                <label className="form-label">Check-in time</label>
                <input
                  type="time"
                  value={formData.checkInTime}
                  onChange={handleInputChange}
                  className="timestamp-input"
                  name="checkInTime"
                />
              </div>

              <div className="timestamp-field">
                <label className="form-label">Check-out time</label>
                <input
                  type="time"
                  value={formData.checkOutTime}
                  onChange={handleInputChange}
                  className="timestamp-input"
                  name="checkOutTime"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label required">Completed all tasks?</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="completedAllTasks"
                    value="yes"
                    checked={formData.completedAllTasks === 'yes'}
                    onChange={handleInputChange}
                  />
                  Yes
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="completedAllTasks"
                    value="no"
                    checked={formData.completedAllTasks === 'no'}
                    onChange={handleInputChange}
                  />
                  No
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label required">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-textarea"
                rows="4"
                placeholder="Describe the service provided and any observations..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Add photos or attachments</label>
              <label className="attachment-picker" htmlFor="job-attachments">
                <input
                  id="job-attachments"
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  multiple
                  capture="environment"
                  onChange={handleAttachmentChange}
                  disabled={uploadingAttachments || removingAttachmentId !== null}
                />
                <span>Tap to add photos or files</span>
              </label>

              {attachments.length > 0 && (
                <div className="attachment-list">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="attachment-item">
                      {attachment.preview ? (
                        <img src={attachment.preview} alt={attachment.name} className="attachment-preview" />
                      ) : (
                        <div className="attachment-file-badge">FILE</div>
                      )}
                      <div className="attachment-meta">
                        <a href={`/api/jobs/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachment.id)}?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`} download={attachment.name}>
                          {attachment.name}
                        </a>
                        <small>{Math.round((attachment.size || 0) / 1024)} KB</small>
                      </div>
                      <button type="button" className="attachment-remove" onClick={() => removeAttachment(attachment.id)} aria-label={`Remove ${attachment.name}`}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {attachmentError && <p role="alert" className="form-error">{attachmentError}</p>}
            </div>

          </>
        )}

        {formData.serviceCompleted === 'no' && (
          <>
            <div className="form-group">
              <label className="form-label required">Attendance outcome</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="attendanceOutcome"
                    value="AttendedPresent"
                    checked={formData.attendanceOutcome === 'AttendedPresent'}
                    onChange={handleInputChange}
                    required
                  />
                  Attended, someone home
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="attendanceOutcome"
                    value="AttendedNotHome"
                    checked={formData.attendanceOutcome === 'AttendedNotHome'}
                    onChange={handleInputChange}
                    required
                  />
                  Attended, no one home
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="attendanceOutcome"
                    value="DidNotAttend"
                    checked={formData.attendanceOutcome === 'DidNotAttend'}
                    onChange={handleInputChange}
                    required
                  />
                  Did not attend
                </label>
              </div>
            </div>

            {formData.attendanceOutcome === 'AttendedNotHome' && (
              <div className="alert-box alert-warning">
                <h3>⚠️ Duty of Care Reminder</h3>
                <ol>
                  <li>Wait 10 minutes</li>
                  <li>Check surroundings: lights, car, curtains, pets</li>
                  <li>Call office: 02 1111 1111</li>
                  <li>DO NOT LEAVE until office staff advise</li>
                  <li>Do not enter. Wait for police if welfare check is needed</li>
                </ol>
              </div>
            )}
            <div className="form-group">
              <label className="form-label" htmlFor="service-canceled">Was the service canceled?</label>
              <select id="service-canceled" name="canceled" value={formData.canceled} onChange={handleInputChange} className="form-select">
                <option value="">No</option>
                <option value="customer">Customer Canceled</option>
                <option value="provider">We Canceled</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label required">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-textarea"
                rows="4"
                placeholder="Explain why the service could not be completed..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Add photos or attachments</label>
              <label className="attachment-picker" htmlFor="job-attachments">
                <input
                  id="job-attachments"
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  multiple
                  capture="environment"
                  onChange={handleAttachmentChange}
                  disabled={uploadingAttachments || removingAttachmentId !== null}
                />
                <span>Tap to add photos or files</span>
              </label>

              {attachments.length > 0 && (
                <div className="attachment-list">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="attachment-item">
                      {attachment.preview ? (
                        <img src={attachment.preview} alt={attachment.name} className="attachment-preview" />
                      ) : (
                        <div className="attachment-file-badge">FILE</div>
                      )}
                      <div className="attachment-meta">
                        <a href={`/api/jobs/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachment.id)}?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`} download={attachment.name}>
                          {attachment.name}
                        </a>
                        <small>{Math.round((attachment.size || 0) / 1024)} KB</small>
                      </div>
                      <button type="button" className="attachment-remove" onClick={() => removeAttachment(attachment.id)} aria-label={`Remove ${attachment.name}`}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {attachmentError && <p role="alert" className="form-error">{attachmentError}</p>}
            </div>
          </>
        )}

        {(formData.serviceCompleted === 'yes' || ['AttendedPresent', 'AttendedNotHome'].includes(formData.attendanceOutcome)) && (
          <WelfareCheck
            serviceId={id}
            initialConcern={formData.welfareConcern}
            initialAnswers={formData.welfareCheck}
            onConcernChange={(value) => setFormData((current) => ({ ...current, welfareConcern: value }))}
            onAnswersChange={(value) => setFormData((current) => ({ ...current, welfareCheck: value }))}
          />
        )}

        <div className="form-actions">
          {saveError && <p role="alert" className="form-error">{saveError}</p>}
          {saveMessage && <p role="status" className="form-success">{saveMessage}</p>}
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving || uploadingAttachments || removingAttachmentId !== null}>
            {uploadingAttachments ? 'Uploading...' : removingAttachmentId ? 'Removing attachment...' : saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceDetails;
